import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Navbar from '../../Navbar';
import Footer from '../../Footer';
import SimuladorRecarga from '../SimuladorRecarga';
import ToolCta from '../ToolCta';
import { fadeInUp } from '../../../lib/animations';

const TOOL_SLUG = 'calculadora-recarga-ev';

/** Página /ferramentas/calculadora-recarga-ev */
const RecargaPage: React.FC = () => (
  <>
    <Helmet>
      <title>Calculadora de Recarga de Carro Elétrico: Tempo e Custo – Gas2</title>
      <meta
        name="description"
        content="Quanto tempo demora recarregar um BYD Dolphin na tomada comum? E quanto custa na conta de luz? Calcule o tempo e o custo de recarga para qualquer EV."
      />
      <link rel="canonical" href={`https://gas2.app/ferramentas/${TOOL_SLUG}`} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Gas2" />
      <meta property="og:url" content={`https://gas2.app/ferramentas/${TOOL_SLUG}`} />
      <meta property="og:title" content="Calculadora de Recarga de Carro Elétrico – Gas2" />
      <meta
        property="og:description"
        content="Calcule o tempo de recarga e o custo na conta de luz para BYD Dolphin, GWM Haval, Volvo XC40 e outros elétricos. Tomada comum, Wallbox ou DC."
      />
      <meta property="og:image" content="https://gas2.app/logo512.png" />
      <meta property="og:locale" content="pt_BR" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Calculadora de Recarga de Carro Elétrico – Gas2" />
      <meta
        name="twitter:description"
        content="Tempo e custo real para recarregar seu elétrico: tomada, Wallbox ou carregador rápido DC."
      />
      <meta name="twitter:image" content="https://gas2.app/logo512.png" />
    </Helmet>

    <Navbar solid />

    <main className="min-h-screen bg-slate-950 pt-24 pb-20">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 space-y-10">

        {/* ── H1 + intro ── */}
        <motion.header
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          className="space-y-4"
        >
          <span className="inline-block rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-600">
            Carros Elétricos
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight">
            Calculadora de Recarga EV: Vai Demorar 1 Hora ou 40 Horas?
          </h1>
          <p className="text-slate-400 text-base sm:text-lg leading-relaxed">
            Uma das maiores dúvidas de quem está pensando em comprar um elétrico é: como
            funciona a recarga no dia a dia? Selecione o modelo, o tipo de carregador e a
            carga atual da bateria. A calculadora mostra o{' '}
            <strong className="text-slate-200">tempo exato até 100%</strong> e o{' '}
            <strong className="text-slate-200">custo real na sua conta de luz</strong> —
            desmistificando de vez a questão da recarga.
          </p>
        </motion.header>

        {/* ── Simulador ── */}
        <SimuladorRecarga />

        {/* ── Conteúdo SEO ── */}
        <motion.article
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="space-y-8"
        >
          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Tomada Comum, Wallbox ou Carregador Rápido: Qual a Diferença?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              A velocidade de recarga depende de dois fatores: a{' '}
              <strong className="text-slate-200">
                potência do carregador (em kW)
              </strong>{' '}
              e a capacidade de aceitação da bateria do veículo. Uma tomada residencial
              comum entrega apenas 1,8–2,3 kW — suficiente para carros pequenos de
              bateria compacta (como o Kwid E-Tech), mas que demora até 40 horas para
              um SUV de bateria grande.
            </p>
            <p className="text-slate-400 leading-relaxed mt-3">
              Um <strong className="text-slate-200">Wallbox residencial de 7,4 kW</strong>{' '}
              resolve a maioria das situações: carrega um BYD Dolphin em 6–8 horas,
              perfeito para deixar carregando durante a noite. Os carregadores rápidos DC
              (50 kW+) encontrados em postos e shoppings fazem o mesmo em menos de 1 hora.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Quanto Custa Recarregar em Casa vs. Posto de Recarga?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              A tarifa residencial de energia no Brasil varia entre R$ 0,70 e R$ 1,20 por
              kWh dependendo da distribuidora e da bandeira tarifária. Um BYD Dolphin com
              bateria de 44,9 kWh custa entre{' '}
              <strong className="text-slate-200">R$ 31 e R$ 54 para uma carga completa</strong>{' '}
              em casa — equivalente a rodar 300–350 km, ou um custo por km de R$ 0,09 a
              R$ 0,18. Nos postos de recarga rápida, o custo por kWh pode ser 3× maior,
              mas o tempo economizado justifica para viagens longas.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Vale a Pena Instalar um Wallbox em Casa?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              Para quem usa o elétrico como carro principal, sim — o investimento se paga
              em praticidade. Um Wallbox de 7,4 kW custa entre R$ 2.500 e R$ 5.000
              instalado. A instalação exige um elétricista qualificado e, dependendo da
              potência contratada na sua residência, pode ser necessário aumentar o
              disjuntor geral. Procure fornecedores certificados pelo INMETRO.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Perguntas Frequentes sobre Recarga de Elétricos
            </h2>
            <div className="space-y-4 mt-3">
              {[
                {
                  q: 'Posso carregar o elétrico na tomada de 220V normal?',
                  a: 'Sim, se o cabo EVSE do fabricante suportar 220V (a maioria suporta). A tomada deve ser aterrada e de uso exclusivo para o veículo. A velocidade será equivalente à tomada comum (1,8–2,3 kW) — lenta, mas funcional para carros de bateria pequena.',
                },
                {
                  q: 'Recarregar sempre até 100% danifica a bateria?',
                  a: 'As baterias de íon-lítio têm vida útil mais longa quando mantidas entre 20% e 80% de carga no cotidiano. Carregar a 100% frequentemente acelera a degradação. Reserve a carga completa para viagens longas.',
                },
                {
                  q: 'Qual é a autonomia real de um elétrico no Brasil?',
                  a: 'A autonomia real costuma ser 15–25% menor do que a divulgada pelo fabricante (medida em ciclo WLTP), especialmente com ar-condicionado ligado em clima quente, tráfego urbano intenso ou em rodovias acima de 100 km/h. A calculadora usa a capacidade da bateria — consulte fóruns do seu modelo para a autonomia real.',
                },
              ].map(({ q, a }) => (
                <div key={q} className="rounded-xl bg-slate-900 border border-slate-800 p-4">
                  <p className="font-semibold text-white text-sm">{q}</p>
                  <p className="text-slate-400 text-sm mt-1 leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </section>
        </motion.article>

        {/* ── CTA download ── */}
        <ToolCta toolSlug={TOOL_SLUG} />
      </div>
    </main>

    <Footer />
  </>
);

export default RecargaPage;



